import { Suspense, lazy, useState } from "react";
import { NavIcon } from "./nav/NavIcon";

/**
 * Same lazy split as `BarcodeField`: the decoder is heavy and most visits to
 * this screen never open a camera, so it stays out of the main bundle.
 */
const BarcodeScanner = lazy(() =>
  import("./BarcodeScanner").then((m) => ({ default: m.BarcodeScanner })),
);

export type InventorySearchCardProps = {
  searchQuery: string;
  onSearchQueryChange: (value: string) => void;
  /** True while the Vases category is the active filter (G-132: the size inputs belong to it). */
  showVesselSize: boolean;
  minDiameterInput: string;
  minDepthInput: string;
  onMinDiameterInputChange: (value: string) => void;
  onMinDepthInputChange: (value: string) => void;
};

const SEARCH_INPUT_ID = "inventory-search";

/**
 * G-132 — the search row: one tall field with its icon inside, and a Scan tile
 * beside it. No card around it (the page is the card), no Refresh (the page
 * re-reads on its own when you come back to it) and no category select (the
 * categories are chips under it). New `inv-search-*` classes on purpose:
 * `.inventory-search-input` is shared with the kiosk finder and stays as it was.
 */
export function InventorySearchCard({
  searchQuery,
  onSearchQueryChange,
  showVesselSize,
  minDiameterInput,
  minDepthInput,
  onMinDiameterInputChange,
  onMinDepthInputChange,
}: InventorySearchCardProps) {
  const [scanning, setScanning] = useState(false);

  return (
    <div className="inv-search">
      <div className="inv-search-row">
        <label className="inv-search-field" htmlFor={SEARCH_INPUT_ID}>
          <span className="visually-hidden">Search inventory</span>
          <NavIcon name="search" size={20} />
          <input
            id={SEARCH_INPUT_ID}
            type="search"
            className="inv-search-input"
            placeholder="Name, barcode, colour"
            value={searchQuery}
            onChange={(e) => onSearchQueryChange(e.target.value)}
            autoComplete="off"
          />
        </label>
        {/* G-83: the staff page's own scan — the code goes into the search box,
            because typing it IS the whole job here. */}
        <button
          type="button"
          className="inv-scan-tile"
          onClick={() => setScanning(true)}
          aria-label="Scan a barcode to search inventory"
        >
          <NavIcon name="barcode" size={22} />
          <span>Scan</span>
        </button>
      </div>

      {scanning ? (
        <Suspense fallback={null}>
          <BarcodeScanner
            onDetected={(code) => {
              onSearchQueryChange(code);
              setScanning(false);
            }}
            onClose={() => setScanning(false)}
          />
        </Suspense>
      ) : null}

      {showVesselSize ? (
        <div className="inv-vessel-size">
          <p className="muted inv-vessel-size-hint">
            Optional: only list items with catalog opening at least this size (cm). Rows with no
            measurement are omitted when a minimum is set.
          </p>
          <div className="inv-vessel-size-fields">
            <div className="inv-vessel-size-field">
              <label htmlFor={`${SEARCH_INPUT_ID}-min-diameter`}>Min. diameter (cm)</label>
              <input
                id={`${SEARCH_INPUT_ID}-min-diameter`}
                type="number"
                min={0}
                step="any"
                inputMode="decimal"
                className="inventory-search-input"
                value={minDiameterInput}
                onChange={(e) => onMinDiameterInputChange(e.target.value)}
                placeholder="Any"
                aria-label="Minimum opening diameter in centimeters"
              />
            </div>
            <div className="inv-vessel-size-field">
              <label htmlFor={`${SEARCH_INPUT_ID}-min-depth`}>Min. depth (cm)</label>
              <input
                id={`${SEARCH_INPUT_ID}-min-depth`}
                type="number"
                min={0}
                step="any"
                inputMode="decimal"
                className="inventory-search-input"
                value={minDepthInput}
                onChange={(e) => onMinDepthInputChange(e.target.value)}
                placeholder="Any"
                aria-label="Minimum depth in centimeters"
              />
            </div>
          </div>
        </div>
      ) : null}
    </div>
  );
}
